/**
 * In-memory fixture client matching the `apiRequest` signature, so the
 * data-tables dashboard is fully browse-able before Workstream H ships.
 *
 * Dev convenience only — guarded by VITE_TABLES_USE_FIXTURES + the
 * MODE !== "production" gate in fixture-flag.ts. Seeded with the realtor
 * story from the feature narrative (Listings + Buyers tables, a
 * matching_listings stored query, a Google Sheet connector, audit rows).
 */

import { AgentsApiError } from "../../../agents/api/client";
import type {
  AdapterSpec,
  AgentQueryBinding,
  AgentTableGrant,
  AttachQueryBindingRequest,
  ColumnInput,
  Connection,
  CreateConnectionRequest,
  CreateRowRequest,
  CreateStoredQueryRequest,
  CreateTableRequest,
  DataAccessAudit,
  DataColumn,
  DataRow,
  DataTable,
  Paged,
  PreviewQueryRequest,
  PreviewResult,
  StoredQuery,
  UpdateColumnRequest,
  UpdateRowRequest,
  UpdateStoredQueryRequest,
  UpdateTableRequest,
  SyncRun,
} from "../types";
import type { EnableSearchRequest, SearchStatus } from "../../search/api/types";

const NOW = () => new Date().toISOString();
const UUID = () =>
  globalThis.crypto?.randomUUID?.() ??
  `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, "0")}`;
const WS_ID = "00000000-0000-4000-8000-000000000001";

interface Store {
  tables: DataTable[];
  rows: Map<string, DataRow[]>; // tableId -> rows
  queries: StoredQuery[];
  adapters: AdapterSpec[];
  connections: Connection[];
  runs: Map<string, SyncRun[]>; // connectionId -> runs
  grants: Map<string, AgentTableGrant[]>; // agentId -> grants
  bindings: Map<string, AgentQueryBinding[]>; // agentId -> bindings
  freeform: Map<string, boolean>; // agentId -> allowFreeform
  audit: DataAccessAudit[];
  search: Map<string, SearchStatus>; // tableId -> semantic-search status
}

function col(
  tableId: string,
  position: number,
  partial: Omit<DataColumn, "id" | "tableId" | "position" | "embeddable"> &
    Partial<Pick<DataColumn, "embeddable">>,
): DataColumn {
  return {
    id: UUID(),
    tableId,
    position,
    embeddable: false,
    ...partial,
  };
}

function seed(): Store {
  const listingsId = "00000000-0000-4000-8000-0000000list1";
  const buyersId = "00000000-0000-4000-8000-00000000buy1";

  const listings: DataTable = {
    id: listingsId,
    workspaceId: WS_ID,
    name: "Listings",
    schemaTable: "listings",
    isLocked: false,
    statusLabels: null,
    source: "custom",
    rowCount: 2,
    columns: [
      col(listingsId, 0, {
        name: "title",
        type: "text",
        nullable: false,
        isIndexed: true,
        isUnique: false,
        isClientLink: false,
      }),
      col(listingsId, 1, {
        name: "price",
        type: "number",
        nullable: false,
        isIndexed: true,
        isUnique: false,
        isClientLink: false,
      }),
      col(listingsId, 2, {
        name: "bedrooms",
        type: "number",
        nullable: true,
        isIndexed: false,
        isUnique: false,
        isClientLink: false,
      }),
      col(listingsId, 3, {
        name: "status",
        type: "select",
        nullable: false,
        isIndexed: false,
        isUnique: false,
        isClientLink: false,
        options: ["available", "under_offer", "sold"],
      }),
    ],
    indexes: [
      { id: UUID(), tableId: listingsId, columns: ["price"], type: "btree", unique: false },
    ],
    createdAt: NOW(),
    updatedAt: NOW(),
  };

  const buyers: DataTable = {
    id: buyersId,
    workspaceId: WS_ID,
    name: "Buyers",
    schemaTable: "buyers",
    isLocked: false,
    statusLabels: null,
    source: "custom",
    rowCount: 1,
    columns: [
      col(buyersId, 0, {
        name: "contact_id",
        type: "text",
        nullable: false,
        isIndexed: true,
        isUnique: true,
        isClientLink: true,
      }),
      col(buyersId, 1, {
        name: "budget",
        type: "number",
        nullable: true,
        isIndexed: false,
        isUnique: false,
        isClientLink: false,
      }),
    ],
    indexes: [],
    createdAt: NOW(),
    updatedAt: NOW(),
  };

  const matching: StoredQuery = {
    id: "00000000-0000-4000-8000-0000000query1",
    workspaceId: WS_ID,
    name: "matching_listings",
    slug: "matching-listings",
    sqlTemplate:
      "SELECT title, price, bedrooms FROM listings WHERE price <= :budget AND status = 'available' ORDER BY price ASC",
    params: [{ name: "budget", type: "number", required: true }],
    isFreeformSelect: false,
    createdAt: NOW(),
    updatedAt: NOW(),
  };

  const sheetAdapter: AdapterSpec = {
    id: "00000000-0000-4000-8000-00000adapter1",
    workspaceId: null,
    name: "Google Sheet",
    category: "none",
    version: "1.0.0",
    connectionMethod: "oauth",
    connectionFields: [{ name: "spreadsheet_url", label: "Spreadsheet URL" }],
  };
  const shopifyAdapter: AdapterSpec = {
    id: "00000000-0000-4000-8000-00000adapter2",
    workspaceId: null,
    name: "Shopify (reference)",
    category: "ecommerce",
    version: "0.1.0",
    connectionMethod: "oauth",
    connectionFields: [{ name: "shop", label: "Shop domain" }],
  };

  const connection: Connection = {
    id: "00000000-0000-4000-8000-0000000000cx1",
    workspaceId: WS_ID,
    adapterId: sheetAdapter.id,
    name: "Demo Sheet",
    status: "connected",
    createdAt: NOW(),
    updatedAt: NOW(),
  };

  const run1: SyncRun = {
    id: UUID(),
    connectionId: connection.id,
    status: "succeeded",
    startedAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    endedAt: new Date(Date.now() - 1000 * 60 * 29).toISOString(),
    rowsUpserted: 12,
    errorMessage: null,
  };
  const run2: SyncRun = {
    id: UUID(),
    connectionId: connection.id,
    status: "skipped",
    startedAt: new Date(Date.now() - 1000 * 60 * 29).toISOString(),
    endedAt: new Date(Date.now() - 1000 * 60 * 29).toISOString(),
    rowsUpserted: null,
    errorMessage: "overlap: previous run still in progress",
  };

  const audit: DataAccessAudit[] = [
    {
      id: UUID(),
      workspaceId: WS_ID,
      agentId: "agent-client-facing",
      runId: "run-abc",
      queryId: matching.id,
      mode: "read",
      delivery: "tool",
      tables: ["listings"],
      sql: 'SELECT * FROM "listings" WHERE price <= $1',
      freeform: false,
      params: { budget: 10000000 },
      rowCount: 2,
      decision: "allowed",
      source: "agent",
      ts: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    },
    {
      id: UUID(),
      workspaceId: WS_ID,
      agentId: "agent-client-facing",
      runId: "run-def",
      queryId: null,
      mode: "write",
      delivery: null,
      tables: ["listings"],
      sql: 'INSERT INTO "listings" (title) VALUES ($1)',
      freeform: true,
      params: { title: "New build" },
      rowCount: 0,
      decision: "denied",
      source: "agent",
      ts: new Date(Date.now() - 1000 * 60 * 2).toISOString(),
    },
  ];

  return {
    tables: [listings, buyers],
    rows: new Map([
      [
        listingsId,
        [
          {
            id: UUID(),
            title: "2BHK Sea View",
            price: 9500000,
            bedrooms: 2,
            status: "available",
          },
          {
            id: UUID(),
            title: "3BHK Garden",
            price: 14500000,
            bedrooms: 3,
            status: "under_offer",
          },
        ],
      ],
      [buyersId, [{ id: UUID(), contact_id: "arjun", budget: 10000000 }]],
    ]),
    queries: [matching],
    adapters: [sheetAdapter, shopifyAdapter],
    connections: [connection],
    runs: new Map([[connection.id, [run2, run1]]]),
    grants: new Map(),
    bindings: new Map(),
    freeform: new Map(),
    audit,
    search: new Map(),
  };
}

const store = seed();

function notFound(message: string): never {
  throw new AgentsApiError({ status: 404, code: "NOT_FOUND", message, requestId: `fix_${UUID()}` });
}

function paged<T>(items: T[]): Paged<T> {
  return { items, nextCursor: null };
}

function segmentsOf(path: string): string[] {
  return (path.split("?")[0] ?? "").split("/").filter(Boolean);
}

function buildColumn(tableId: string, input: ColumnInput, position: number): DataColumn {
  return col(tableId, position, {
    name: input.name,
    type: input.type,
    nullable: input.nullable,
    isIndexed: input.isIndexed ?? false,
    isUnique: input.isUnique ?? false,
    isClientLink: input.isClientLink ?? false,
    options: input.options,
  });
}

function handleTables<T>(method: string, segments: string[], body: unknown): T {
  const tableId = segments[2];

  if (!tableId) {
    if (method === "GET") return paged(store.tables) as T;
    if (method === "POST") {
      const req = body as CreateTableRequest;
      const id = UUID();
      const created: DataTable = {
        id,
        workspaceId: WS_ID,
        name: req.name,
        schemaTable: req.name.toLowerCase().replace(/[^a-z0-9_]+/g, "_"),
        isLocked: false,
        statusLabels: null,
        source: "custom",
        rowCount: 0,
        columns: req.columns.map((c, i) => buildColumn(id, c, i)),
        indexes: [],
        createdAt: NOW(),
        updatedAt: NOW(),
      };
      store.tables.unshift(created);
      store.rows.set(id, []);
      return created as T;
    }
  }

  const idx = tableId ? store.tables.findIndex((tb) => tb.id === tableId) : -1;
  if (tableId && idx < 0) notFound(`Fixture: table ${tableId} not found`);
  const table = store.tables[idx]!;

  // /api/tables/:id
  if (tableId && segments.length === 3) {
    if (method === "GET") return table as T;
    if (method === "PATCH") {
      const req = body as UpdateTableRequest;
      const updated = { ...table, ...req, updatedAt: NOW() };
      store.tables[idx] = updated;
      return updated as T;
    }
    if (method === "DELETE") {
      store.tables.splice(idx, 1);
      store.rows.delete(tableId);
      return undefined as T;
    }
  }

  // /api/tables/:id/columns[/:colId]
  if (tableId && segments[3] === "columns") {
    const colId = segments[4];
    if (!colId && method === "POST") {
      const created = buildColumn(tableId, body as ColumnInput, table.columns.length);
      table.columns.push(created);
      table.updatedAt = NOW();
      return created as T;
    }
    if (colId) {
      const cIdx = table.columns.findIndex((c) => c.id === colId);
      if (cIdx < 0) notFound(`Fixture: column ${colId} not found`);
      if (method === "PATCH") {
        const updated = { ...table.columns[cIdx]!, ...(body as UpdateColumnRequest) };
        table.columns[cIdx] = updated;
        table.updatedAt = NOW();
        return updated as T;
      }
      if (method === "DELETE") {
        table.columns.splice(cIdx, 1);
        table.updatedAt = NOW();
        return undefined as T;
      }
    }
  }

  // /api/tables/:id/rows[/:rowId]
  if (tableId && segments[3] === "rows") {
    const rows = store.rows.get(tableId) ?? [];
    const rowId = segments[4];
    if (!rowId && method === "GET") return paged(rows) as T;
    if (!rowId && method === "POST") {
      const created: DataRow = { id: UUID(), ...(body as CreateRowRequest).values };
      store.rows.set(tableId, [...rows, created]);
      store.tables[idx] = { ...table, rowCount: rows.length + 1, updatedAt: NOW() };
      return created as T;
    }
    if (rowId) {
      const rIdx = rows.findIndex((r) => r.id === rowId);
      if (rIdx < 0) notFound(`Fixture: row ${rowId} not found`);
      if (method === "PATCH") {
        const updated: DataRow = { ...rows[rIdx]!, ...(body as UpdateRowRequest).values };
        rows[rIdx] = updated;
        return updated as T;
      }
      if (method === "DELETE") {
        store.rows.set(
          tableId,
          rows.filter((r) => r.id !== rowId),
        );
        store.tables[idx] = { ...table, rowCount: Math.max(0, rows.length - 1), updatedAt: NOW() };
        return undefined as T;
      }
    }
  }

  // /api/tables/:id/search/{status,suggestions,enable}
  if (tableId && segments[3] === "search") {
    const kind = segments[4];
    const textColumns = table.columns.filter((c) => c.type === "text");

    if (kind === "suggestions" && method === "GET") {
      return {
        suggestions: textColumns.map((c) => ({
          column: c.name,
          reason: `Long free-text column "${c.displayName ?? c.name}" — good for semantic search.`,
        })),
      } as T;
    }

    if (kind === "status" && method === "GET") {
      const existing = store.search.get(tableId);
      const status: SearchStatus = existing ?? {
        enabled: false,
        embeddedColumns: [],
        facetColumns: [],
        rerankMinScore: 0.3,
        maxLimit: 50,
        minRerankFloor: 0.3,
        rerankPoolCap: 200,
        backfillState: "idle",
        coverage: { total: table.rowCount, embedded: 0 },
        rerank: false,
        rerankAvailable: true,
      };
      return status as T;
    }

    if (kind === "enable" && method === "POST") {
      const req = body as EnableSearchRequest;
      const status: SearchStatus = {
        enabled: true,
        embeddedColumns: req.columns,
        facetColumns: req.facetColumns,
        rerankMinScore: req.rerankMinScore ?? 0.3,
        maxLimit: req.maxLimit ?? 50,
        minRerankFloor: req.minRerankFloor ?? 0.3,
        rerankPoolCap: req.rerankPoolCap ?? 200,
        backfillState: "done",
        coverage: { total: table.rowCount, embedded: table.rowCount },
        rerank: req.rerank,
        rerankAvailable: true,
      };
      store.search.set(tableId, status);
      return status as T;
    }

    if (kind === "enable" && method === "DELETE") {
      store.search.delete(tableId);
      return undefined as T;
    }
  }

  notFound(`Fixture: ${method} /${segments.join("/")}`);
}

function handleDataQueries<T>(method: string, segments: string[], body: unknown): T {
  // /api/data-queries/preview
  if (segments[2] === "preview" && method === "POST") {
    const req = body as PreviewQueryRequest;
    const isWrite = /\b(insert|update|delete)\b/i.test(req.sql);
    const result: PreviewResult = {
      rows: isWrite ? [] : (store.rows.get(store.tables[0]?.id ?? "") ?? []).slice(0, 5),
      rowCount: isWrite ? 1 : Math.min(5, store.rows.get(store.tables[0]?.id ?? "")?.length ?? 0),
      durationMs: 12,
      resolvedSql: req.sql.replace(/:(\w+)/g, (_m, p1: string) =>
        JSON.stringify(req.args[p1] ?? null),
      ),
      committed: isWrite ? !req.dryRunWrites : false,
    };
    return result as T;
  }

  const queryId = segments[2];
  if (!queryId) {
    if (method === "GET") return paged(store.queries) as T;
    if (method === "POST") {
      const req = body as CreateStoredQueryRequest;
      const created: StoredQuery = {
        id: UUID(),
        workspaceId: WS_ID,
        name: req.name,
        slug: req.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        sqlTemplate: req.sqlTemplate,
        params: req.params,
        isFreeformSelect: req.isFreeformSelect,
        createdAt: NOW(),
        updatedAt: NOW(),
      };
      store.queries.unshift(created);
      return created as T;
    }
  }

  const idx = queryId ? store.queries.findIndex((q) => q.id === queryId) : -1;
  if (queryId && idx < 0) notFound(`Fixture: query ${queryId} not found`);
  const query = store.queries[idx]!;

  if (method === "GET") return query as T;
  if (method === "PATCH") {
    const updated = { ...query, ...(body as UpdateStoredQueryRequest), updatedAt: NOW() };
    store.queries[idx] = updated;
    return updated as T;
  }
  if (method === "DELETE") {
    store.queries.splice(idx, 1);
    return undefined as T;
  }

  notFound(`Fixture: ${method} /${segments.join("/")}`);
}

function handleAgentData<T>(method: string, segments: string[], body: unknown): T {
  const agentId = segments[2] ?? "";
  const kind = segments[3];

  if (kind === "table-grants") {
    const list = store.grants.get(agentId) ?? [];
    const tableId = segments[4];
    if (!tableId && method === "GET") return paged(list) as T;
    if (!tableId && method === "POST") {
      // The mutation snakeizes the body before dispatch, so read snake_case keys.
      const req = body as {
        table_id: string;
        can_find?: boolean;
        can_insert?: boolean;
        can_update?: boolean;
        can_delete?: boolean;
        use_rerank?: boolean;
        search_max_limit?: number | null;
        search_min_floor?: number | null;
        search_pool_cap?: number | null;
        find_max_rows?: number | null;
      };
      const table = store.tables.find((tb) => tb.id === req.table_id);
      const grant: AgentTableGrant = {
        agentId,
        tableId: req.table_id,
        tableName: table?.name ?? req.table_id,
        attachedVia: "manual",
        mode: req.can_insert || req.can_update || req.can_delete ? "write" : "read",
        canFind: Boolean(req.can_find),
        canInsert: Boolean(req.can_insert),
        canUpdate: Boolean(req.can_update),
        canDelete: Boolean(req.can_delete),
        useRerank: req.use_rerank ?? true,
        searchMaxLimit: req.search_max_limit ?? null,
        searchMinFloor: req.search_min_floor ?? null,
        searchPoolCap: req.search_pool_cap ?? null,
        findMaxRows: req.find_max_rows ?? null,
      };
      store.grants.set(agentId, [...list.filter((g) => g.tableId !== req.table_id), grant]);
      return grant as T;
    }
    if (tableId && method === "DELETE") {
      store.grants.set(
        agentId,
        list.filter((g) => g.tableId !== tableId),
      );
      return undefined as T;
    }
  }

  if (kind === "query-bindings") {
    const list = store.bindings.get(agentId) ?? [];
    const qId = segments[4];
    if (!qId && method === "GET") return paged(list) as T;
    if (!qId && method === "POST") {
      const req = body as AttachQueryBindingRequest;
      const sq = store.queries.find((q) => q.id === req.storedQueryId);
      const binding: AgentQueryBinding = {
        agentId,
        storedQueryId: req.storedQueryId,
        name: sq?.name ?? req.storedQueryId,
        attachedVia: "manual",
        mode: req.mode,
        delivery: req.delivery,
        paramMap: req.paramMap,
        template: req.template,
        cadence: req.cadence,
        rowCap: req.rowCap,
      };
      store.bindings.set(agentId, [
        ...list.filter((b) => b.storedQueryId !== req.storedQueryId),
        binding,
      ]);
      return binding as T;
    }
    if (qId && method === "DELETE") {
      store.bindings.set(
        agentId,
        list.filter((b) => b.storedQueryId !== qId),
      );
      return undefined as T;
    }
  }

  if (kind === "allow-freeform" && method === "PATCH") {
    store.freeform.set(agentId, Boolean((body as { allowFreeform: boolean }).allowFreeform));
    return undefined as T;
  }

  notFound(`Fixture: ${method} /${segments.join("/")}`);
}

function handleConnectors<T>(method: string, segments: string[], body: unknown): T {
  // Catalog (bare array) at the collection root; connections at /connections.
  if (!segments[2]) {
    if (method === "GET") return store.adapters as T;
    if (method === "POST") {
      const req = body as CreateConnectionRequest;
      const created: Connection = {
        id: UUID(),
        workspaceId: WS_ID,
        adapterId: req.adapterId,
        name: req.name ?? "Connection",
        status: "connected",
        createdAt: NOW(),
        updatedAt: NOW(),
      };
      store.connections.unshift(created);
      store.runs.set(created.id, []);
      return created as T;
    }
  }

  if (segments[2] === "connections" && method === "GET") return store.connections as T;

  // OAuth connect kickoff: /api/connectors/:adapterId/oauth/initiate.
  if (segments[3] === "oauth" && segments[4] === "initiate" && method === "POST") {
    return { consent_url: `https://provider.example/oauth?adapter=${segments[2]}` } as T;
  }

  const connectionId = segments[2];
  const idx = connectionId ? store.connections.findIndex((c) => c.id === connectionId) : -1;
  if (connectionId && idx < 0) notFound(`Fixture: connector ${connectionId} not found`);

  if (connectionId && segments.length === 3 && method === "DELETE") {
    store.connections.splice(idx, 1);
    store.runs.delete(connectionId);
    return undefined as T;
  }

  if (connectionId && segments[3] === "runs" && method === "GET") {
    return paged(store.runs.get(connectionId) ?? []) as T;
  }

  if (connectionId && segments[3] === "sync" && method === "POST") {
    const run: SyncRun = {
      id: UUID(),
      connectionId,
      status: "succeeded",
      startedAt: NOW(),
      endedAt: NOW(),
      rowsUpserted: Math.floor(Math.random() * 20),
      errorMessage: null,
    };
    store.runs.set(connectionId, [run, ...(store.runs.get(connectionId) ?? [])]);
    return run as T;
  }

  notFound(`Fixture: ${method} /${segments.join("/")}`);
}

function handleLogs<T>(method: string): T {
  if (method === "GET") return paged(store.audit) as T;
  notFound(`Fixture: ${method} /api/logs`);
}

export async function fixtureApiRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const segments = segmentsOf(path);
  if (segments[0] !== "api") notFound(`Fixture: unknown path ${path}`);

  switch (segments[1]) {
    case "tables":
      return handleTables<T>(method, segments, body);
    case "data-queries":
      return handleDataQueries<T>(method, segments, body);
    case "agents":
      return handleAgentData<T>(method, segments, body);
    case "connectors":
      return handleConnectors<T>(method, segments, body);
    case "logs":
      return handleLogs<T>(method);
    default:
      notFound(`Fixture: ${method} ${path}`);
  }
}
